import React from "react"
import { TextField, Dialog, FlatButton, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn, Checkbox } from "material-ui"
import strings from "../../../../localization/strings"
import autobind from "class-autobind"
import { IAccountManagerUserDocument, IAddAccountManagerUser } from "../../../../models"
import AuthenticationStore from "../../../../stores/AuthenticationStore"
import * as utils from "../../../../helpers/utils"
import * as permissionHelper from "../../../../helpers/permissionHelper"
import { grey700 } from "material-ui/styles/colors"
import _ from "lodash"
import PasswordField from "../../../common/PasswordField"

interface IProps {
	editUser: IAccountManagerUserDocument
	handleAdd(user: IAddAccountManagerUser): void
	handleUpdate(user: IAddAccountManagerUser, userId: string): void
	close(): void
}
interface IState {
	firstName: string
	firstNameErrorText?: string
	lastName: string
	lastNameErrorText?: string
	email: string
	emailErrorText?: string
	password: string
	passwordErrorText?: string
	passwordVerified?: boolean
	passwordConfirmation: string
	passwordConfirmationErrorText?: string
	permissions: string[]
	roleRowOrder: string[][]
}

export default class AccountManagerUsersTabAddUserDialog extends React.Component<IProps, IState> {
	private allPermissions: string[]
	private roleRowColCount
	private roleOrderForAllFeature
	constructor(props: IProps) {
		super(props)
		this.state = {
			firstName: props.editUser ? props.editUser.FirstName : "",
			lastName: props.editUser ? props.editUser.LastName : "",
			email: props.editUser ? props.editUser.Email : "",
			password: "",
			passwordConfirmation: "",
			permissions: props.editUser && props.editUser.UserRole && props.editUser.UserRole.Permissions ? props.editUser.UserRole.Permissions : [],
			roleRowOrder: [],
		}
		autobind(this)
		this.allPermissions = permissionHelper.getAllAccountManagerPermissions()
		this.roleRowColCount = 2
		this.roleOrderForAllFeature = [
			{ name: "Account", colCount: 1 },
			{ name: "Clients", colCount: 1 },
		]
	}

	componentDidMount() {
		this.setRoleRowOrder()
	}

	setRoleRowOrder() {
		let roleOrder = this.roleOrderForAllFeature.slice()

		const roleRowOrder = []
		let totalColCountOfRole = 0
		roleOrder.forEach((role) => {
			if (totalColCountOfRole === 0) {
				roleRowOrder.push([role.name])
				totalColCountOfRole += role.colCount
			}
			else if (totalColCountOfRole + role.colCount > this.roleRowColCount) {
				roleRowOrder.push([role.name])
				totalColCountOfRole = role.colCount
			}
			else {
				roleRowOrder[roleRowOrder.length - 1].push(role.name)
				totalColCountOfRole += role.colCount
			}
		})
		this.setState({ roleRowOrder })
	}

	detectEnter(event) {
		event.keyCode === 13 && this.handleSave()
	}

	handleSave() {
		if (!this.checkValidate()) return
		else {
			const user = {
				FirstName: this.state.firstName,
				LastName: this.state.lastName,
				Email: this.state.email,
			} as IAddAccountManagerUser

			if (this.props.editUser) {
				if (this.state.password !== "") user.Password = this.state.password
				user.UserRole = { ...this.props.editUser.UserRole, Permissions: this.state.permissions }
				this.props.handleUpdate(user, this.props.editUser.Id)
			}
			else {
				user.Password = this.state.password
				user.UserRole = { Id: utils.guid(), Type: "", Permissions: this.state.permissions }
				this.props.handleAdd(user)
			}
		}
	}

	checkValidate() {
		let returnVal = true

		if (!this.state.firstName || this.state.firstName.length === 0) {
			this.setState({ firstNameErrorText: strings.First_Name_cannot_be_empty })
			returnVal = false
		} else {
			this.setState({ firstNameErrorText: "" })
		}

		if (!this.state.lastName || this.state.lastName.length === 0) {
			this.setState({ lastNameErrorText: strings.Last_Name_cannot_be_empty })
			returnVal = false
		} else {
			this.setState({ lastNameErrorText: "" })
		}

		if (utils.validateEmail(this.state.email)) {
			this.setState({ emailErrorText: "" })
		} else {
			this.setState({ emailErrorText: strings.Invalid_email_format })
			returnVal = false
		}

		//if we are editing, do not require a password. If we don't have a password set, then it won't update.
		if (!this.props.editUser && !this.state.password) {
			this.setState({ passwordErrorText: strings.Password_required })
			returnVal = false
		}
		else {
			this.setState({ passwordErrorText: "" })
			if (this.state.password && !this.state.passwordVerified) {
				returnVal = false
			}
		}

		if (this.state.password !== this.state.passwordConfirmation) {
			this.setState({ passwordConfirmationErrorText: strings.Passwords_must_match })
			returnVal = false
		} else {
			this.setState({ passwordConfirmationErrorText: "" })
		}

		if (!this.state.email) {
			this.setState({ emailErrorText: strings.Email_cannot_be_empty })
			returnVal = false
		}
		else {
			this.setState({ emailErrorText: "" })
		}

		return returnVal
	}

	handleCheck(e, checked) {
		const permission = e.target.name
		if (permission === "AllPermissions" && !checked) {
			if (this.props.editUser && this.props.editUser.Id === AuthenticationStore.getLoggedInAccountManagerUser().Id) {
				this.setState({ permissions: [AuthenticationStore.AccountManagerUserPermissions.Account.ViewAacountManagerUsersTab] })
			}
			else this.setState({ permissions: [] })
		}
		else if (permission === "AllPermissions" && checked) {
			this.setState({ permissions: this.allPermissions })
		}

		else if (permission === AuthenticationStore.AccountManagerUserPermissions.Clients.ViewClients && !checked) {
			this.setState({ permissions: this.state.permissions.filter(x => !Object.values(AuthenticationStore.AccountManagerUserPermissions.Clients).includes(x)) })
		}

		else if (Object.values(AuthenticationStore.AccountManagerUserPermissions.Clients).filter(x => x !== AuthenticationStore.AccountManagerUserPermissions.Clients.ViewClients).includes(permission) && checked) {
			this.setState({ permissions: _.uniq([...this.state.permissions, permission, AuthenticationStore.AccountManagerUserPermissions.Clients.ViewClients]) })
		}

		else if (!checked) {
			this.setState({ permissions: this.state.permissions.filter(x => x !== permission) })
		}

		else if (checked) {
			this.setState({ permissions: [...this.state.permissions, permission] })
		}
	}

	render() {
		return (
			<Dialog
				open
				contentStyle={{ maxWidth: 1000, width: 900 }}
				title={this.props.editUser ? `${strings.Edit} ${strings.Account_Manager_Users.slice(0, -1)}` : `${strings.Add} ${strings.Account_Manager_Users.slice(0, -1)}`}
				actions={[
					<FlatButton
						label={strings.CANCEL}
						secondary
						onTouchTap={this.props.close}
					/>,
					<FlatButton
						primary
						label={this.props.editUser ? strings.Update : strings.Save}
						style={{ display: "inline-block" }}
						onTouchTap={this.handleSave}
					/>
				]}
			>
				<div>
					<div style={{ color: grey700, fontSize: 18 }}>{strings.User_Details}</div>
					<div style={{ width: "100%" }}>
						<div style={{ height: 60, display: "inline-block", verticalAlign: "top", width: "30%", marginRight: 25 }}>
							<TextField
								autoFocus={!this.props.editUser ? true : false}
								floatingLabelText="First Name"
								value={this.state.firstName}
								onChange={e => this.setState({ firstName: e.target.value }, () => this.checkValidate())}
								onKeyUp={this.detectEnter}
								errorText={this.state.firstNameErrorText}
								style={{ width: "100%" }}
							/>
						</div>
						<div style={{ height: 60, display: "inline-block", verticalAlign: "top", width: "30%", marginRight: 25 }}>
							<TextField
								floatingLabelText="Last Name"
								value={this.state.lastName}
								onChange={e => this.setState({ lastName: e.target.value }, () => this.checkValidate())}
								onKeyUp={this.detectEnter}
								errorText={this.state.lastNameErrorText}
								style={{ width: "100%" }}
							/>
						</div>
						<div style={{ height: 60, display: "inline-block", verticalAlign: "top", width: "30%", marginRight: 25 }}>
							<TextField
								floatingLabelText="Email"
								value={this.state.email}
								onChange={e => this.setState({ email: e.target.value, emailErrorText: utils.validateEmail(e.target.value) ? "" : strings.Invalid_email_format })}
								onKeyUp={this.detectEnter}
								errorText={this.state.emailErrorText}
								style={{ width: "100%" }}
							/>
						</div>
					</div>
					<div style={{ display: "flex", flexWrap: "wrap" }}>
						<div style={{ width: "30%", marginRight: 25 }}>
							<PasswordField
								password={this.state.password}
								errorPassword={this.state.passwordErrorText}
								handleChange={(password, passwordVerified) => this.setState({ password, passwordVerified }, () => this.checkValidate())}
								handleSave={(password, passwordVerified) => this.setState({ password, passwordVerified }, () => this.checkValidate())}
							/>
						</div>
						<div style={{ width: "30%" }}>
							<TextField
								autoComplete="new-password"
								floatingLabelText="Password Confirmation"
								value={this.state.passwordConfirmation}
								onChange={e => this.setState({ passwordConfirmation: e.target.value }, () => this.checkValidate())}
								onKeyUp={this.detectEnter}
								type="password"
								errorText={this.state.passwordConfirmationErrorText}
								style={{ width: "100%" }}
							/>
						</div>
					</div>
					<div style={{ marginTop: 45 }}>
						<div style={{ color: grey700, fontSize: 18 }}>{strings.User_Permissions}</div>
						{this.state.roleRowOrder.map((row, index) =>
							<Table
								key={index}
								selectable={false}
								multiSelectable={false}
								style={{ backgroundColor: "inherit" }}
							>
								<TableHeader displaySelectAll={false} adjustForCheckbox={false} enableSelectAll={false} style={{ height: 20 }}>
									<TableRow style={{ height: 20 }}>
										{row.map((roleName, roleIndex) => {
											const columns = []
											for (let i = 0; i < this.roleOrderForAllFeature.find((role) => role.name === roleName).colCount; i++) {
												columns.push(<TableHeaderColumn style={styles.colHeader} key={`${roleIndex}-${i}`}>{i === 0 && strings[roleName]}</TableHeaderColumn>)
											}
											return columns
										})}
									</TableRow>
								</TableHeader>
								<TableBody displayRowCheckbox={false}>
									<TableRow>
										{row.includes("Account") &&
											<TableRowColumn style={styles.col}>
												<Checkbox
													name="AllPermissions"
													label={strings.All_Permissions}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.allPermissions.length === this.state.permissions.length}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Account.ViewAacountManagerUsersTab}
													label={strings.View_Account_Manager_Users_Tab}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Account.ViewAacountManagerUsersTab)}
													disabled={this.props.editUser && this.props.editUser.Id === AuthenticationStore.getLoggedInAccountManagerUser().Id}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Account.ViewSystemTab}
													label={strings.View_System_Tab}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Account.ViewSystemTab)}
												/>
											</TableRowColumn>
										}

										{row.includes("Clients") &&
											<TableRowColumn style={styles.col}>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.ViewClients}
													label={strings.View_Account_Manager_Clients}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.ViewClients)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.CreateClientAccount}
													label={strings.Add_Account_Manager_Client_Account}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.CreateClientAccount)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.EditAccountManagerClientDetails}
													label={strings.Edit_Account_Manager_Client_Details}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.EditAccountManagerClientDetails)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.EnterClientAccount}
													label={strings.Enter_Client_Account}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.EnterClientAccount)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.AssignPotentialClientRelationshipManager}
													label={strings.AssignPotentialClientRelationshipManager}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.AssignPotentialClientRelationshipManager)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.AccountScheduledJob}
													label={strings.Account_Scheduled_Job}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.AccountScheduledJob)}
												/>
												<Checkbox
													name={AuthenticationStore.AccountManagerUserPermissions.Clients.AccountFeatureSetting}
													label={strings.Account_Feature_Setting}
													style={styles.checkBox}
													iconStyle={styles.input}
													onCheck={this.handleCheck}
													checked={this.state.permissions.includes(AuthenticationStore.AccountManagerUserPermissions.Clients.AccountFeatureSetting)}
												/>
											</TableRowColumn>
										}
									</TableRow>
								</TableBody>
							</Table>
						)}
					</div>
				</div>
			</Dialog>
		)
	}
}

const styles = {
	colHeader: { padding: 5, height: 25 },
	col: { paddingLeft: 5, paddingRight: 5, verticalAlign: "initial" },
	checkBox: { marginTop: 5, marginBottom: 5 },
	input: { marginRight: 5 }
}